import { ArrowLeft, Eye, Minimize2, PenLine, X } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router";

import type { Status, TicketDetail } from "../api/types";
import { can, useMe } from "../auth/session";
import { formatDateTime } from "../lib/format";
import { useHotkeys, useMediaQuery } from "../lib/hotkeys";
import { usePageTitle } from "../lib/hooks";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { ToggleRow } from "../ui/Field";
import { ErrorState, Notice } from "../ui/Notice";
import { PageHeader } from "../ui/PageHeader";
import { Select } from "../ui/Select";
import { Spinner } from "../ui/Spinner";
import { useLiveTicket, usePresence } from "../live/live";
import { presenceText, type Present } from "../live/presence";
import { Avatar } from "../ui/Avatar";
import {
  useAddWatcher,
  useMarkTicketRead,
  useRemoveWatcher,
  useTicket,
  useTicketAction,
  useTimeline,
} from "./api";
import { Composer } from "./Composer";
import {
  AssignDialog,
  ChangeError,
  ClassifyDialog,
  DuplicateDialog,
  PriorityDialog,
  ReopenDialog,
  ResolveDialog,
} from "./dialogs";
import {
  ENVIRONMENT_LABELS,
  IMPACT_LABELS,
  NEXT_STATUSES,
  PRIORITY_LABELS,
  CLOSE_WORDING,
  describeEvent,
  RESOLUTION_LABELS,
  SOURCE_LABELS,
  STATUS_LABELS,
  STATUS_TONES,
  TYPE_LABELS,
  URGENCY_LABELS,
} from "./labels";
import { personChoice } from "./AssignTarget";
import { DirectoryPicker } from "./DirectoryPicker";
import { listAddress, SPLIT_QUERY, withOpen } from "./split";
import { PriorityBadge, RestrictedBadge, StatusBadge } from "./TicketBadges";
import { AttachmentLinks, Timeline } from "./Timeline";
import "./tickets.css";

/** Opening a ticket counts as reading its conversation. */
function useMarkSeen(reference: string, items: number | undefined) {
  const { mutate } = useMarkTicketRead();
  useEffect(() => {
    if (items !== undefined) mutate(reference);
  }, [reference, items, mutate]);
}

type DialogName = "assign" | "resolve" | "reopen" | "priority" | "classify" | "duplicate";

export function TicketPage() {
  const { reference = "" } = useParams();
  const ticket = useTicket(reference);
  usePageTitle(ticket.data ? `${ticket.data.reference} ${ticket.data.title}` : reference);
  return <TicketView reference={reference} />;
}

/**
 * One ticket: its own page, or the side pane beside the tickets table (DECISIONS #53).
 * Support staff see who else has it open (DECISIONS #51).
 */
export function TicketView({ reference, embedded = false }: { reference: string; embedded?: boolean }) {
  const [params, setParams] = useSearchParams();
  const ticket = useTicket(reference);
  const me = useMe();
  const timeline = useTimeline(reference);
  useLiveTicket(reference);
  useMarkSeen(reference, timeline.data?.length);
  const { others, typing } = usePresence(reference, can(me, "use_workspace"));

  const reload = () => {
    void ticket.refetch();
    void timeline.refetch();
  };

  if (ticket.isPending) {
    return <Spinner label="Loading ticket" />;
  }
  if (ticket.isError) {
    return (
      <div className="card">
        <ErrorState error={ticket.error} onRetry={reload} />
      </div>
    );
  }

  const t = ticket.data;
  const created = !embedded && params.get("created") === "1";
  const uploadFailed = params.get("upload_failed");

  return (
    <div className={embedded ? "ticket-view ticket-view-embedded" : "ticket-view ticket-view-page"}>
      {embedded ? null : <PageBar reference={t.reference} />}
      {created ? (
        <div className="ticket-notice">
          <Notice tone="success">
            Ticket <strong>{t.reference}</strong> raised. We'll update you here{me.email_updates ? " and by email" : ""}.{" "}
            <button type="button" className="link-btn" onClick={() => setParams({}, { replace: true })}>
              Dismiss
            </button>
          </Notice>
          {uploadFailed ? <Notice tone="error">Your files weren't attached: {uploadFailed} Add them in a reply.</Notice> : null}
        </div>
      ) : null}
      <TicketHeader ticket={t} />
      <PresenceBar others={others} />
      <div className="ticket-layout">
        <Conversation ticket={t} timeline={timeline} onTyping={typing} onReload={reload} />
        <aside className="card ticket-side" aria-label="Ticket details">
          <DetailsSection ticket={t} onReload={reload} />
          {t.app_context ? <AppContextSection context={t.app_context} /> : null}
          <PeopleSection ticket={t} />
        </aside>
      </div>
    </div>
  );
}

/**
 * The conversation box: the ticket's actions along the top, the request as its opening message,
 * everything since in order, the outcome where it happened, and the reply box docked at the bottom. On a desktop it is the one
 * part of the ticket that scrolls, and it opens at the latest activity.
 */
function Conversation({
  ticket,
  timeline,
  onTyping,
  onReload,
}: {
  ticket: TicketDetail;
  timeline: ReturnType<typeof useTimeline>;
  onTyping: () => void;
  onReload: () => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const items = timeline.data;
  const created = items?.find((item) => item.kind === "created");
  const sender = ticket.email_from;
  const latest = items?.length ?? 0;
  const atBottom = useRef(true);

  // Open at the latest activity, and follow new activity unless someone has scrolled up to read.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, [ticket.reference, latest, ticket.status]);
  useEffect(() => {
    atBottom.current = true;
  }, [ticket.reference]);

  return (
    <section className="card ticket-thread" aria-labelledby="thread-heading">
      <h2 id="thread-heading" className="visually-hidden">
        Conversation
      </h2>
      <TicketActions ticket={ticket} onReload={onReload} />
      <div
        ref={scroller}
        className="thread-scroll"
        onScroll={(event) => {
          const el = event.currentTarget;
          atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        }}
      >
        <article className="message message-opening" aria-label="Request">
          <Avatar name={sender?.name ?? ticket.created_by.name} seed={ticket.created_by.id} size="sm" />
          <div className="message-body">
            <div className="message-meta">
              {sender ? (
                <>
                  <span className="cell-strong">{sender.name}</span>
                  <span className="muted">
                    emailed from <a href={`mailto:${sender.email}`}>{sender.email}</a>
                  </span>
                  {/* An outside sender's email, made into a ticket by whoever reviewed it. */}
                  {sender.name !== ticket.created_by.name ? (
                    <span className="muted">added by {ticket.created_by.name}</span>
                  ) : null}
                </>
              ) : (
                <>
                  <span className="cell-strong">{ticket.created_by.name}</span>
                  <span className="muted">{created ? describeEvent(created) : "raised this ticket"}</span>
                </>
              )}
              <time className="muted" dateTime={ticket.created_at}>
                {formatDateTime(ticket.created_at)}
              </time>
            </div>
            <p className="message-text">{ticket.description}</p>
            <AttachmentLinks reference={ticket.reference} attachments={ticket.attachments.filter((a) => !a.comment_id)} />
          </div>
        </article>
        {timeline.isPending ? (
          <Spinner label="Loading history" />
        ) : timeline.isError ? (
          <ErrorState error={timeline.error} onRetry={() => void timeline.refetch()} />
        ) : (
          <Timeline reference={ticket.reference} items={timeline.data.filter((item) => item !== created)} />
        )}
        {ticket.resolution ? <ResolutionCard ticket={ticket} /> : null}
      </div>
      <div className="thread-composer">
        {ticket.status === "closed" && ticket.can.reopen ? (
          <Notice tone="info">This ticket is closed. Reopen it if the problem is back.</Notice>
        ) : (
          <Composer key={ticket.reference} ticket={ticket} onTyping={onTyping} docked />
        )}
      </div>
    </section>
  );
}

/** Who else from support has this ticket open, and whether they're writing (DECISIONS #51). */
function PresenceBar({ others }: { others: Present[] }) {
  if (!others.length) return null;
  const writing = others.some((p) => p.state === "typing");
  const Icon = writing ? PenLine : Eye;
  return (
    <p className={writing ? "presence presence-writing" : "presence"} role="status">
      <span className="presence-faces" aria-hidden="true">
        {others.slice(0, 3).map((p) => (
          <Avatar key={p.id} name={p.name} seed={p.id} size="sm" />
        ))}
      </span>
      <Icon size={14} aria-hidden="true" />
      {presenceText(others)}
    </p>
  );
}

/**
 * Above a ticket on its own page: back to the list, and for staff on a wide screen, a button to
 * show it beside the list again, on the list the expand button brought them from.
 */
function PageBar({ reference }: { reference: string }) {
  const me = useMe();
  const staff = can(me, "use_workspace");
  const wide = useMediaQuery(SPLIT_QUERY);
  const list = listAddress(useLocation().state) ?? "/tickets";
  const home = new URL(list, window.location.origin).pathname === "/";
  return (
    <div className="ticket-page-bar">
      <Link to={withOpen(list, null)} className="back-link">
        <ArrowLeft size={16} aria-hidden="true" />
        {home ? "Home" : staff ? "Tickets" : "Your tickets"}
      </Link>
      {staff && wide ? (
        <Link
          to={withOpen(list, reference)}
          className="icon-btn"
          aria-label="Show beside the list"
          data-tooltip="Show beside the list"
          data-tooltip-align="end"
        >
          <Minimize2 size={16} aria-hidden="true" />
        </Link>
      ) : null}
    </div>
  );
}

/** The title block: what the ticket is, where it stands, and who raised it. */
function TicketHeader({ ticket }: { ticket: TicketDetail }) {
  return (
    <PageHeader
      eyebrow={ticket.reference}
      title={ticket.title}
      titleExtra={
        <span className="badges">
          <StatusBadge status={ticket.status} />
          <PriorityBadge priority={ticket.priority} />
          {ticket.is_restricted ? <RestrictedBadge /> : null}
          {ticket.overdue ? <Badge tone="danger">Overdue</Badge> : null}
        </span>
      }
      sub={`${TYPE_LABELS[ticket.type]} raised by ${raisedBy(ticket)} on ${formatDateTime(ticket.created_at)}`}
    />
  );
}

/**
 * Everything you can do to the ticket, along the top of the conversation box: the smaller
 * changes on the left, status, assigning and resolving on the right. Nothing shows if the
 * person can do none of it.
 */
function TicketActions({ ticket, onReload }: { ticket: TicketDetail; onReload: () => void }) {
  const [dialog, setDialog] = useState<DialogName | null>(null);
  const close = useTicketAction<object>(ticket.reference, "close");
  const status = useTicketAction<{ status: Status }>(ticket.reference, "status");
  const next = ticket.can.change_status ? NEXT_STATUSES[ticket.status] : [];
  const dialogProps = { ticket, onClose: () => setDialog(null), onReload };
  // E resolves, A assigns (DECISIONS #53).
  useHotkeys({
    e: () => (ticket.can.resolve ? setDialog("resolve") : undefined),
    a: () => (ticket.can.assign ? setDialog("assign") : undefined),
  });
  const tools: [DialogName, string, boolean][] = [
    ["priority", "Priority", ticket.can.set_priority],
    ["classify", "Service", ticket.can.reclassify],
    ["duplicate", "Mark duplicate", ticket.can.mark_duplicate],
  ];
  const shownTools = tools.filter(([, , allowed]) => allowed);
  const main = next.length || ticket.can.assign || ticket.can.resolve || ticket.can.close || ticket.can.reopen;
  if (!shownTools.length && !main) return null;

  return (
    <div className="thread-toolbar">
      <div className="thread-toolbar-row">
        <div className="thread-tools" role="group" aria-label="More actions">
          {shownTools.map(([name, label]) => (
            <Button key={name} size="sm" variant="ghost" onClick={() => setDialog(name)}>
              {label}
            </Button>
          ))}
        </div>
        <div className="thread-actions">
          {/* Where the box is narrow, the smaller changes fold into one menu (tickets.css). */}
          {shownTools.length ? (
            <Select
              label="More actions"
              className="thread-more"
              size="sm"
              value=""
              placeholder="More"
              onChange={(name) => setDialog(name as DialogName)}
              groups={[{ options: shownTools.map(([name, label]) => ({ value: name, label })) }]}
            />
          ) : null}
          {next.length ? (
            <Select
              label="Change status"
              className="status-select"
              size="sm"
              value=""
              placeholder="Change status"
              disabled={status.isPending}
              onChange={(value) => status.mutate({ version: ticket.version, status: value as Status })}
              groups={[{ options: next.map((st) => ({ value: st, label: STATUS_LABELS[st], tone: STATUS_TONES[st] })) }]}
            />
          ) : null}
          {ticket.can.assign ? (
            <Button size="sm" onClick={() => setDialog("assign")}>
              {ticket.owner ? "Reassign" : "Assign"}
            </Button>
          ) : null}
          {ticket.can.resolve ? (
            <Button size="sm" variant="primary" onClick={() => setDialog("resolve")}>
              Resolve
            </Button>
          ) : null}
          {ticket.can.close ? (
            <Button size="sm" variant="primary" loading={close.isPending} onClick={() => close.mutate({ version: ticket.version })}>
              {ticket.resolution ? CLOSE_WORDING[ticket.resolution.code].button : "Confirm it's fixed"}
            </Button>
          ) : null}
          {ticket.can.reopen ? (
            <Button size="sm" onClick={() => setDialog("reopen")}>
              Reopen
            </Button>
          ) : null}
        </div>
      </div>
      <ChangeError error={status.error ?? close.error} onReload={onReload} />
      {dialog === "assign" ? <AssignDialog {...dialogProps} /> : null}
      {dialog === "resolve" ? <ResolveDialog {...dialogProps} /> : null}
      {dialog === "reopen" ? <ReopenDialog {...dialogProps} /> : null}
      {dialog === "priority" ? <PriorityDialog {...dialogProps} /> : null}
      {dialog === "classify" ? <ClassifyDialog {...dialogProps} /> : null}
      {dialog === "duplicate" ? <DuplicateDialog {...dialogProps} /> : null}
    </div>
  );
}

function ResolutionCard({ ticket }: { ticket: TicketDetail }) {
  const r = ticket.resolution;
  if (!r) return null;
  const rows: [string, string | null][] = [
    ["Cause", r.cause],
    // Tickets resolved before the two boxes became one (DECISIONS #56) have both.
    ["What was done", r.steps],
    [r.steps ? "Outcome" : "What was done", r.outcome],
    ["Confirmed by", r.verification],
  ];
  return (
    <section className="resolution-card" aria-labelledby="resolution-heading">
      <div className="card-head">
        <h2 id="resolution-heading" className="card-title">
          {RESOLUTION_LABELS[r.code]}
        </h2>
        <span className="card-sub">
          {r.resolved_by ? `By ${r.resolved_by.name} on ` : ""}
          {formatDateTime(r.resolved_at)}
        </span>
      </div>
      {ticket.duplicate_of ? (
        <p>
          Duplicate of <Link to={`/tickets/${ticket.duplicate_of}`}>{ticket.duplicate_of}</Link>
        </p>
      ) : null}
      <dl className="resolution-fields">
        {rows
          .filter(([, value]) => value)
          .map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
      </dl>
      {ticket.can.close ? (
        <p className="muted">{CLOSE_WORDING[r.code].prompt} It closes automatically after 5 working days.</p>
      ) : null}
    </section>
  );
}

function DetailsSection({ ticket, onReload }: { ticket: TicketDetail; onReload: () => void }) {
  const restrict = useTicketAction<{ is_restricted: boolean }>(ticket.reference, "restricted");
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Status and priority are beside the title already.
  const rows: [string, ReactNode][] = [
    ["Service", ticket.service ? `${ticket.service.name}${ticket.category ? ` › ${ticket.category.name}` : ""}` : "Not sure yet"],
    ["Group", ticket.group?.name ?? "None"],
    ["Owner", ticket.owner?.name ?? "Nobody yet"],
    ["Raised via", SOURCE_LABELS[ticket.source] ?? ticket.source],
  ];
  if (ticket.status !== "resolved" && ticket.status !== "closed") {
    rows.push(["Due", ticket.due_at ? formatDateTime(ticket.due_at) : "—"]);
  }
  if (ticket.can.view_internal) {
    rows.push(
      ["Impact", IMPACT_LABELS[ticket.impact]],
      ["Urgency", URGENCY_LABELS[ticket.urgency]],
    );
    // Only worth a line when someone changed the priority from what the form suggested.
    if (ticket.suggested_priority !== ticket.priority) {
      rows.push(["Suggested", PRIORITY_LABELS[ticket.suggested_priority]]);
    }
    if (ticket.priority_reason) rows.push(["Override reason", ticket.priority_reason]);
  }
  if (ticket.duplicates.length) {
    rows.push([
      "Duplicates",
      <span key="d" className="badges">
        {ticket.duplicates.map((ref) => (
          <Link key={ref} to={`/tickets/${ref}`}>
            {ref}
          </Link>
        ))}
      </span>,
    ]);
  }
  return (
    <section className="side-section" aria-labelledby="details-heading">
      <h2 id="details-heading" className="side-title">
        Details
      </h2>
      <dl className="detail-list">
        {rows.map(([label, value]) => (
          <div key={label} className="detail-row">
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {ticket.can.set_restricted ? (
        <div className="toggle-list">
          <ToggleRow
            label="Confidential"
            description="Only the people on it and admins can see it."
            checked={ticket.is_restricted}
            disabled={restrict.isPending}
            onChange={(checked) =>
              // Opening a ticket up is the risky direction, so it asks first.
              checked ? restrict.mutate({ version: ticket.version, is_restricted: true }) : setConfirmOpen(true)
            }
          />
        </div>
      ) : null}
      <ChangeError error={restrict.error} onReload={onReload} />
      <Dialog
        open={confirmOpen}
        title="Make this ticket visible?"
        onClose={() => setConfirmOpen(false)}
        footer={
          <>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              loading={restrict.isPending}
              onClick={() =>
                restrict.mutate(
                  { version: ticket.version, is_restricted: false },
                  { onSettled: () => setConfirmOpen(false) },
                )
              }
            >
              Make visible
            </Button>
          </>
        }
      >
        <p>All support staff will be able to view it, including internal notes.</p>
      </Dialog>
    </section>
  );
}

const CONTEXT_LABELS: Record<string, string> = {
  app: "App",
  version: "Version",
  environment: "Environment",
  screen: "Screen",
  correlation_id: "Correlation ID",
};

/** What the app sent with its Support link (staff only). */
function AppContextSection({ context }: { context: Record<string, string> }) {
  return (
    <section className="side-section" aria-labelledby="app-context-heading">
      <h2 id="app-context-heading" className="side-title">
        From the app
      </h2>
      <dl className="detail-list">
        {Object.entries(context).map(([key, value]) => (
          <div key={key} className="detail-row">
            <dt>{CONTEXT_LABELS[key] ?? key}</dt>
            <dd className={key === "screen" || key === "correlation_id" ? "detail-mono" : undefined}>
              {key === "environment" ? (ENVIRONMENT_LABELS[value] ?? value) : value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function PeopleSection({ ticket }: { ticket: TicketDetail }) {
  const add = useAddWatcher(ticket.reference);
  const remove = useRemoveWatcher(ticket.reference);
  const me = useMe();
  return (
    <section className="side-section" aria-labelledby="people-heading">
      <h2 id="people-heading" className="side-title">
        People
      </h2>
      <dl className="detail-list">
        <div className="detail-row">
          <dt>Requester</dt>
          <dd>{ticket.requester.name}</dd>
        </div>
        {ticket.created_by.id !== ticket.requester.id ? (
          <div className="detail-row">
            <dt>Raised by</dt>
            <dd>{ticket.created_by.name}</dd>
          </div>
        ) : null}
      </dl>
      <h3 className="side-subheading">Shared with</h3>
      {ticket.watchers.length ? (
        <ul className="watcher-list">
          {ticket.watchers.map((w) => (
            <li key={w.id}>
              <span>{w.name}</span>
              {ticket.can.manage_watchers || w.id === me.id ? (
                <button
                  type="button"
                  className="icon-btn icon-btn-sm"
                  aria-label={w.id === me.id ? "Leave this ticket" : `Remove ${w.name}`}
                  onClick={() => remove.mutate(w.id)}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : ticket.can.manage_watchers ? null : (
        <p className="muted">Nobody yet.</p>
      )}
      {ticket.can.manage_watchers ? (
        <DirectoryPicker
          key={ticket.watchers.length}
          label="Add someone"
          hideLabel
          placeholder="Add someone"
          value={null}
          onChange={(person) => {
            if (!person || person.user_id === ticket.requester.id) return;
            if (ticket.watchers.some((w) => w.id === person.user_id)) return;
            add.mutate(personChoice(person));
          }}
        />
      ) : null}
      {add.isError || remove.isError ? <ChangeError error={add.error ?? remove.error} onReload={() => undefined} /> : null}
    </section>
  );
}

/** "Sam Lee", or "Leah Brandt for Sam Lee" when staff raised it for someone else. */
function raisedBy(ticket: TicketDetail): string {
  return ticket.created_by.id === ticket.requester.id
    ? ticket.requester.name
    : `${ticket.created_by.name} for ${ticket.requester.name}`;
}
